/*
 * hercase.vercel.app · a water ring-toss portfolio by Hernan Case · source: github.com/hercase/hercase-playground
 * MIT, with attribution: a site built from this one should say "Inspired by Hernan Case, hercase.vercel.app"
 * where visitors can see it. Say hi: hercase92@gmail.com
 */
import { useEffect, useRef, useState } from 'react'
import { FiArrowUpRight, FiChevronDown, FiMail, FiPlay, FiX } from 'react-icons/fi'
import { trackEvent } from '../../lib/analytics'

const FOCUSABLE = 'a[href], button, [tabindex]:not([tabindex="-1"])'
/** A sheet dragged down this far (px) is dismissed. */
const SWIPE_CLOSE_PX = 90

interface AboutPanelProps {
  open: boolean
  onClose: () => void
}

/**
 * The profile card. One component, two presentations: a centred card from tablets up, a
 * bottom sheet on phones (drag handle, swipe down to dismiss). Both pin the header and the
 * action bar and scroll only the middle, so the name and Email are always in view. The
 * "Watch me work" joke is a disclosure row inside the content, never a floating popover.
 */
export const AboutPanel: React.FC<AboutPanelProps> = ({ open, onClose }) => {
  const panelRef = useRef<HTMLDivElement>(null)
  const demoRef = useRef<HTMLDivElement>(null)
  const [demo, setDemo] = useState(false)
  const [dragY, setDragY] = useState(0)
  const dragStart = useRef<number | null>(null)

  useEffect(() => {
    if (!open) {
      setDemo(false)
      setDragY(0)
      return
    }
    const panel = panelRef.current
    panel?.focus({ preventScroll: true })

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
        return
      }
      // Keep Tab inside the dialog.
      if (e.key === 'Tab' && panel) {
        const nodes = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE))
        if (nodes.length === 0) return
        const first = nodes[0]
        const last = nodes[nodes.length - 1]
        const active = document.activeElement
        if (e.shiftKey && (active === first || active === panel)) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && active === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  // The video, once opened, is brought into view: on a phone it sits at the end of the body.
  useEffect(() => {
    if (demo) demoRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [demo])

  if (!open) return null

  // Swipe down on the header to dismiss the sheet (phones only: the card has no such gesture).
  const isSheet = () => !window.matchMedia('(min-width: 640px)').matches
  const onDragStart = (e: React.PointerEvent) => {
    if (!isSheet() || (e.target as HTMLElement).closest('button')) return
    dragStart.current = e.clientY
    e.currentTarget.setPointerCapture(e.pointerId)
  }
  const onDragMove = (e: React.PointerEvent) => {
    if (dragStart.current === null) return
    setDragY(Math.max(0, e.clientY - dragStart.current))
  }
  const onDragEnd = () => {
    if (dragStart.current === null) return
    dragStart.current = null
    if (dragY > SWIPE_CLOSE_PX) onClose()
    else setDragY(0)
  }

  const linkBase =
    'min-h-11 items-center gap-1.5 rounded-full px-3 text-sm text-white/80 underline decoration-white/25 underline-offset-4 transition-colors duration-200 hover:text-white hover:decoration-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/70'
  const link = `inline-flex ${linkBase}`
  const label = 'text-[11px] font-medium tracking-[0.2em] text-white/45 uppercase sm:pt-0.5'
  const dragging = dragStart.current !== null

  return (
    <div
      className="fixed inset-0 z-30 flex items-end justify-center bg-black/70 backdrop-blur-sm animate-fade-in motion-reduce:animate-none sm:items-center sm:p-4"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="about-title"
        onClick={(e) => e.stopPropagation()}
        className={`relative flex h-[94dvh] w-full flex-col overflow-hidden rounded-t-3xl select-text border border-white/10 bg-gradient-to-b from-[#121216] to-[#09090b] text-white outline-none animate-rise motion-reduce:animate-none sm:h-auto sm:max-h-[calc(100dvh-2rem)] sm:max-w-[560px] sm:rounded-3xl lg:max-w-[820px] ${
          dragging ? '' : 'transition-transform duration-200 ease-out'
        }`}
        style={{
          boxShadow: '0 1px 0 0 hsla(0, 0%, 100%, 0.08) inset, 0 30px 80px -20px rgba(0, 0, 0, 0.8)',
          transform: dragY ? `translateY(${dragY}px)` : undefined,
        }}
      >
        {/* Header: pinned. On a phone it is also the handle to swipe the sheet away. */}
        <header
          className="shrink-0 touch-none border-b border-white/8 px-5 pt-3 pb-4 sm:touch-auto sm:px-9 sm:pt-7 sm:pb-5"
          onPointerDown={onDragStart}
          onPointerMove={onDragMove}
          onPointerUp={onDragEnd}
          onPointerCancel={onDragEnd}
        >
          <span aria-hidden="true" className="mx-auto mb-3 block h-1 w-10 rounded-full bg-white/20 sm:hidden" />
          <div className="flex items-center justify-between">
            {/* The brand's three colours as a small mark. */}
            <p className="flex items-center gap-3 text-[11px] font-medium tracking-[0.3em] text-white/45 uppercase">
              <span aria-hidden="true" className="flex gap-1">
                <span className="h-1.5 w-1.5 rounded-full bg-[#ff2bff]" />
                <span className="h-1.5 w-1.5 rounded-full bg-[#2bf5ff]" />
                <span className="h-1.5 w-1.5 rounded-full bg-[#ffef3a]" />
              </span>
              About
            </p>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="-mr-2 flex h-11 w-11 cursor-pointer items-center justify-center rounded-full text-white/60 transition-colors duration-200 hover:bg-white/10 hover:text-white focus-visible:outline-2 focus-visible:outline-white/70"
            >
              <FiX size={20} />
            </button>
          </div>
          <h2 id="about-title" className="mt-1 text-2xl font-semibold tracking-tight md:text-3xl">
            Hernan Case
          </h2>
          <p className="mt-1 text-sm text-white/60 md:text-base">Tech Lead · Principal Software Engineer at DEPT®</p>
        </header>

        {/* Body: the only part that scrolls. Two columns where there is room: the pitch and the
            video on the left, the fact sheet on the right. */}
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-5 sm:px-9 sm:py-6 lg:grid lg:grid-cols-[1fr_1.2fr] lg:gap-x-10 lg:py-7">
          <div>
            <p className="max-w-[52ch] text-[15px] leading-relaxed text-white/80 sm:text-base">
              Fifteen years shipping for global brands. I lead the architecture, the team and the delivery.
            </p>

            <ul className="mt-4 flex flex-wrap gap-2" aria-label="What I do">
              {['Technical leadership', 'Discovery to launch', 'Headless and commerce', 'AI-assisted engineering'].map(
                (item) => (
                  <li
                    key={item}
                    className="rounded-full border border-white/12 bg-white/[0.04] px-3 py-1 text-xs text-white/80 sm:py-1.5 sm:text-[13px]"
                  >
                    {item}
                  </li>
                )
              )}
            </ul>

            {/* The joke: a "live demo" row that turns out to be the monkey, unfolding in place. */}
            <div
              ref={demoRef}
              className="mt-5 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] scroll-mb-4"
            >
              <button
                type="button"
                onClick={() =>
                  setDemo((d) => {
                    if (!d) trackEvent('watch_me_work')
                    return !d
                  })
                }
                aria-expanded={demo}
                aria-controls="about-demo"
                className="flex min-h-14 w-full cursor-pointer items-center gap-3 px-4 py-3 text-left transition-colors duration-200 hover:bg-white/[0.04] focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-white/70"
              >
                <span
                  aria-hidden="true"
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-black"
                >
                  <FiPlay size={14} className="ml-0.5" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-white/90">Watch me work</span>
                  <span className="block text-xs text-white/50">Four seconds on loop, no sound</span>
                </span>
                <FiChevronDown
                  size={18}
                  aria-hidden="true"
                  className={`shrink-0 text-white/50 transition-transform duration-200 ${demo ? 'rotate-180' : ''}`}
                />
              </button>
              {demo && (
                <figure id="about-demo" className="px-2 pb-2 animate-fade-in motion-reduce:animate-none">
                  <video
                    width={480}
                    height={270}
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="metadata"
                    aria-label="A monkey typing frantically on a laptop"
                    className="aspect-video w-full rounded-xl object-cover"
                  >
                    <source src="/monkey-typing.webm" type="video/webm" />
                    <source src="/monkey-typing.mp4" type="video/mp4" />
                  </video>
                  <figcaption className="px-1 pt-2 pb-1 text-center text-[13px] text-white/75">
                    Literally me while coding.
                  </figcaption>
                </figure>
              )}
            </div>
            <a
              href="/llms.txt"
              target="_blank"
              rel="noopener"
              onClick={() => trackEvent('link_click', { link: 'Full profile', from: 'about' })}
              aria-label="Full profile (opens in a new tab)"
              className={`${link} mt-3 -ml-3 sm:hidden`}
            >
              Full profile
            </a>
          </div>
          <dl className="mt-5 lg:mt-0 grid gap-x-6 gap-y-2.5 text-[13px] sm:gap-y-3 sm:text-sm md:grid-cols-[auto_1fr]">
            <dt className={label}>Stack</dt>
            <dd className="text-white/80">
              React, Next.js, TypeScript, Node · Headless CMS and commerce: Sanity, Contentful, Contentstack,
              commercetools · Vercel, Netlify, AWS, GCP · GraphQL, Storybook, Jest, Cypress, GitHub Actions
            </dd>
            <dt className={label}>Clients</dt>
            <dd className="text-white/80">
              Global brands in food service, streaming, athleisure, energy, pet care and luxury retail. Agency work
              under NDA: names on LinkedIn, code stays private.
            </dd>
            <dt className={label}>Works</dt>
            <dd className="text-white/80">Remote, from Argentina (UTC−3) · English and Spanish</dd>
            <dt className={label}>Open to</dt>
            <dd className="text-white/80">Tech lead, staff and principal frontend roles</dd>
          </dl>
        </div>

        {/* Actions: pinned, so Email is one tap away however long the body is. */}
        <footer className="flex shrink-0 flex-wrap items-center gap-2 border-t border-white/8 px-5 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))] sm:px-9 sm:pt-4 sm:pb-6">
          <a
            href="mailto:hercase92@gmail.com?subject=Hi%20Hernan"
            onClick={() => trackEvent('link_click', { link: 'Email', from: 'about' })}
            className="inline-flex min-h-11 items-center gap-2 rounded-full bg-white px-5 text-sm font-medium text-black transition-[background-color,transform] duration-200 hover:bg-white/90 active:scale-[0.98] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/70"
          >
            <FiMail size={16} aria-hidden="true" />
            Email
          </a>
          <a
            href="https://www.linkedin.com/in/hercase/"
            target="_blank"
            rel="noreferrer"
            onClick={() => trackEvent('link_click', { link: 'LinkedIn', from: 'about' })}
            aria-label="LinkedIn (opens in a new tab)"
            className={link}
          >
            LinkedIn
            <FiArrowUpRight size={14} aria-hidden="true" />
          </a>
          <a
            href="/llms.txt"
            target="_blank"
            rel="noopener"
            onClick={() => trackEvent('link_click', { link: 'Full profile', from: 'about' })}
            aria-label="Full profile (opens in a new tab)"
            className={`${linkBase} ml-auto hidden sm:inline-flex`}
          >
            Full profile
          </a>
        </footer>
      </div>
    </div>
  )
}
